import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, Component, Input, effect, inject, signal } from '@angular/core';
import { NgKLoggerService } from '@kin/ngk-logger';

import '@kin/web-components/dist/external/kin-alert/kin-alert';
import { QuoteDraftStore } from 'app/global-store/quote-draft';

import { mapUserFriendlyErrorMessage } from './alert.utils';
import { PHONE_NUMBERS } from '../../constants/contact-constants';
import { PhonePipe } from '../../pipes/phone-number/phone.pipe';

/**
 * Alert Component
 *
 * Informs the user of an error that occurred during the quote process.
 * Attempts to map the error message to a user-friendly format;
 * falls back to a generic message if mapping unavailable.
 *
 * Kinetic docs for <kin-alert>: https://ui.kin.insure/components/alert
 *
 */
@Component({
  selector: 'app-alert',
  imports: [PhonePipe],
  template: `
    <kin-alert open closable variant="danger">
      <b>Uh oh! Something went wrong.</b><br />
      {{ errorResponseSignal() || message }} <br />Contact us at <a class="link" [href]="'tel: ' + phoneNumber">{{ phoneNumber | phone }}</a>
    </kin-alert>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AlertComponent {
  quoteDraftStore = inject(QuoteDraftStore);
  logger = inject(NgKLoggerService);

  phoneNumber = PHONE_NUMBERS.sales;

  @Input() message = 'There was a problem processing your information. Please double check what was entered and try again or contact support.';

  readonly errorResponseSignal = signal<string | undefined>(undefined);

  constructor() {
    effect(() => {
      const errorResponse = this.quoteDraftStore.errorResponse();
      if (errorResponse) {
        this.errorResponseSignal.set(mapUserFriendlyErrorMessage(errorResponse));
      }
    });
  }
}
